import { array } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { CheckInConfigurationType, CheckInConfigurationValue } from '@mews-commander/data-access';
import { CheckInConfigurationValues, CheckInFieldsValues, DocumentTypeField } from '../types';
import { documentFields, documentSectionFields } from '../sections';
import { getCheckInSameValues } from '../utils';

/* we only care about reservationOwner value for the documentType fields */
export const getSelectedDocumentType = (values: CheckInFieldsValues): DocumentTypeField | null => {
    const isEverythingDefault = pipe(
        documentSectionFields,
        array.every((field) => values[field].reservationOwner === CheckInConfigurationValue.Default)
    );

    if (isEverythingDefault) {
        return null;
    }

    const nonDoNotShowField = documentSectionFields.find(
        (fieldType) => values[fieldType].reservationOwner !== CheckInConfigurationValue.DoNotShow
    );

    return nonDoNotShowField != null && isDocumentTypeField(nonDoNotShowField)
        ? nonDoNotShowField
        : CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense;
};

const resetDocumentFields = () => {
    const resettedFields = pipe(
        documentFields,
        array.map(({ type }) => [type, getCheckInSameValues(CheckInConfigurationValue.Default)]),
        Object.fromEntries
    );

    const resettedDocumentTypesFields = pipe(
        documentSectionFields,
        array.map((type) => [type, getCheckInSameValues(CheckInConfigurationValue.Default)]),
        Object.fromEntries
    );

    return { ...resettedFields, ...resettedDocumentTypesFields };
};

const getDocumentTypeChanges = flow(
    (selectedDocumentType: CheckInConfigurationType) => {
        switch (selectedDocumentType) {
            case CheckInConfigurationType.PassportOrIdentityCard:
                return [
                    CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense,
                    CheckInConfigurationType.DirectPassport,
                    CheckInConfigurationType.DirectIdentityCard,
                ];
            case CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense:
                return [
                    CheckInConfigurationType.PassportOrIdentityCard,
                    CheckInConfigurationType.DirectPassport,
                    CheckInConfigurationType.DirectIdentityCard,
                    CheckInConfigurationType.DirectDriversLicense,
                ];

            case CheckInConfigurationType.DirectPassport:
            case CheckInConfigurationType.DirectIdentityCard:
                return [
                    CheckInConfigurationType.PassportOrIdentityCard,
                    CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense,
                ];
            case CheckInConfigurationType.DirectDriversLicense:
                return [CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense];
            default:
                return [];
        }
    },
    array.map((documentType) => [documentType, getCheckInSameValues(CheckInConfigurationValue.DoNotShow)]),
    Object.fromEntries
);

interface ChangeDocumentVisibilityParams {
    selectedDocumentType: DocumentTypeField | null;
    fields: CheckInFieldsValues;
    visibility: CheckInConfigurationValue;
    enhancedCheckInFormDocumentsSectionEnabled: boolean;
}

const identityCardFields: CheckInConfigurationType[] = [
    CheckInConfigurationType.IdentityCardIssuingCity,
    CheckInConfigurationType.IdentityCardIssuingCountry,
    CheckInConfigurationType.IdentityCardIssueDate,
    CheckInConfigurationType.IdentityCardExpirationDate,
];

const passportFields: CheckInConfigurationType[] = [
    CheckInConfigurationType.PassportIssuingCity,
    CheckInConfigurationType.PassportIssuingCountry,
    CheckInConfigurationType.PassportIssueDate,
    CheckInConfigurationType.PassportExpirationDate,
];

export const driversLicenseFields: CheckInConfigurationType[] = [
    CheckInConfigurationType.DriversLicenseIssuingCity,
    CheckInConfigurationType.DriversLicenseIssuingCountry,
    CheckInConfigurationType.DriversLicenseIssueDate,
    CheckInConfigurationType.DriversLicenseExpirationDate,
];

export const allDocumentFields = [...identityCardFields, ...passportFields, ...driversLicenseFields];

export const documentTypeToFields: Record<DocumentTypeField, CheckInConfigurationType[]> = {
    [CheckInConfigurationType.PassportOrIdentityCard]: [...identityCardFields, ...passportFields],
    [CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense]: allDocumentFields,
    [CheckInConfigurationType.DirectPassport]: passportFields,
    [CheckInConfigurationType.DirectIdentityCard]: identityCardFields,
    [CheckInConfigurationType.DirectDriversLicense]: driversLicenseFields,
};

export const changeDocumentVisibility = ({
    selectedDocumentType,
    fields,
    visibility,
    enhancedCheckInFormDocumentsSectionEnabled,
}: ChangeDocumentVisibilityParams) => {
    /** Changing document visibility of null is not allowed by UI — the Visibility field is disabled */
    if (selectedDocumentType == null) {
        return fields;
    }

    const isValueRequiredOrOptional = [
        CheckInConfigurationValue.Required,
        CheckInConfigurationValue.Optional,
        ...(enhancedCheckInFormDocumentsSectionEnabled ? [CheckInConfigurationValue.Default] : []),
    ].includes(visibility);
    const visibilityObject = getCheckInSameValues(visibility);

    if (isValueRequiredOrOptional) {
        const changes = getDocumentTypeChanges(selectedDocumentType);
        return { ...fields, ...changes, [selectedDocumentType]: visibilityObject };
    }

    return { ...fields, [selectedDocumentType]: visibilityObject };
};

export const changeDocumentType = (documentType: CheckInConfigurationType | null, fields: CheckInFieldsValues) =>
    documentType == null ? { ...fields, ...resetDocumentFields() } : fields;

const configurationValuePriorityOrder = [
    CheckInConfigurationValue.Required,
    CheckInConfigurationValue.Optional,
    CheckInConfigurationValue.DoNotShow,
    CheckInConfigurationValue.Default,
];

export const hasParentFieldValueHigherPriority = (
    parentFieldValue: CheckInConfigurationValue,
    childFieldValue: CheckInConfigurationValue
) => {
    return (
        configurationValuePriorityOrder.indexOf(childFieldValue) <
        configurationValuePriorityOrder.indexOf(parentFieldValue)
    );
};

export const getFieldValueByParent = (values: CheckInConfigurationValues, parentValue: CheckInConfigurationValue) => {
    const getChildValue = (childFieldValue: CheckInConfigurationValue) => {
        return hasParentFieldValueHigherPriority(parentValue, childFieldValue) ? parentValue : childFieldValue;
    };

    return {
        reservationOwner: getChildValue(values.reservationOwner),
        reservationAdult: getChildValue(values.reservationAdult),
        reservationChild: getChildValue(values.reservationChild),
    };
};

export const documentTypeFields = [
    CheckInConfigurationType.PassportOrIdentityCard,
    CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense,
    CheckInConfigurationType.DirectPassport,
    CheckInConfigurationType.DirectDriversLicense,
    CheckInConfigurationType.DirectIdentityCard,
];
export const isDocumentTypeField = (field: CheckInConfigurationType): field is DocumentTypeField =>
    documentTypeFields.includes(field);

interface CalculateDocumentVisibilityChangeParams {
    selectedDocumentType: DocumentTypeField;
    oldCheckInFieldsValues: CheckInFieldsValues;
    visibility: CheckInConfigurationValue;
    enhancedCheckInFormDocumentsSectionEnabled: boolean;
}

export const calculateDocumentVisibilityChange = ({
    selectedDocumentType,
    oldCheckInFieldsValues,
    visibility,
    enhancedCheckInFormDocumentsSectionEnabled,
}: CalculateDocumentVisibilityChangeParams): CheckInFieldsValues => {
    // update the parent row
    const updatedParentValues = changeDocumentVisibility({
        selectedDocumentType,
        fields: oldCheckInFieldsValues,
        visibility,
        enhancedCheckInFormDocumentsSectionEnabled,
    });

    const parentValue = updatedParentValues[selectedDocumentType].reservationOwner;

    // figure out which child-sections need bumping
    const fieldsToChange = documentTypeToFields[selectedDocumentType];
    const childrenFieldsChanges = Object.fromEntries(
        fieldsToChange
            .filter((childType) => {
                const childRow = updatedParentValues[childType];
                // if any of owner/adult/child is lower than parentValue → sync it
                return ['reservationOwner', 'reservationAdult', 'reservationChild'].some((col) =>
                    hasParentFieldValueHigherPriority(parentValue, childRow[col])
                );
            })
            .map((childType) => [childType, getFieldValueByParent(updatedParentValues[childType], parentValue)])
    );

    return {
        ...updatedParentValues,
        ...(enhancedCheckInFormDocumentsSectionEnabled ? childrenFieldsChanges : {}),
    };
};
